import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Instagram, ExternalLink, Sparkles, Download, Loader2, MessageCircle, Send } from 'lucide-react';
import { buildStoryGraphic } from '../utils/buildStoryGraphic';

/**
 * InstagramShareModal
 * ─────────────────────────────────────────────────────────────────
 * Bottom-sheet modal that previews exactly how the Story-link sticker
 * will look on Instagram and gives the user a one-tap copy + deep link
 * into the IG app. Mounted via React Portal so it escapes any parent
 * stacking context (same pattern as VideoCaseStudyModal).
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   shareUrl      — the OG-rich URL to paste into IG (REQUIRED)
 *   title         — story title
 *   subtitle      — short hook (1 sentence)
 *   image         — absolute cover image URL
 *   brandLabel    — "Dreams League" / "FLYT Studios" etc.
 *   destinationHost — domain that will appear under the sticker preview
 */
const InstagramShareModal = ({
  open,
  onClose,
  shareUrl,
  title,
  subtitle,
  image,
  brandLabel = 'Dreams League',
  destinationHost = 'flythouse.com',
}) => {
  const [copyState, setCopyState] = useState('idle'); // idle | copied
  const [imgError, setImgError] = useState(false);
  const [storyState, setStoryState] = useState('idle'); // idle | generating | shared | downloaded | error
  const [storyErr, setStoryErr] = useState('');
  const [msgState, setMsgState] = useState('idle'); // idle | sharing | sent | copied | error

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setCopyState('copied'); } catch { /* ignore */ }
      ta.remove();
    } finally {
      setTimeout(() => setCopyState('idle'), 1800);
    }
  };

  const handleOpenInstagram = async () => {
    // Copy first so the user just needs to paste once IG is open
    try { await navigator.clipboard.writeText(shareUrl); setCopyState('copied'); } catch { /* ignore */ }

    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);

    if (isMobile) {
      // Try the IG deep link → fall back to web after a beat
      const t = setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      }, 700);
      try {
        window.location.href = 'instagram://story-camera';
        // If the deep link fires, the visibilitychange will clear the fallback
        const cancel = () => { clearTimeout(t); document.removeEventListener('visibilitychange', cancel); };
        document.addEventListener('visibilitychange', cancel);
      } catch {
        clearTimeout(t);
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }
  };

  // ── Story-graphic share/download ──────────────────────────────────
  // Builds a 1080x1920 PNG that mirrors the in-app preview card, then
  // (a) tries native Web Share with files — on iOS Safari this hands
  // off straight to the Instagram → Stories flow with the image
  // pre-loaded, and (b) falls back to downloading the PNG to camera
  // roll so the user can manually attach it.
  const handleShareStoryGraphic = async () => {
    if (storyState === 'generating') return;
    setStoryErr('');
    setStoryState('generating');
    try {
      const { file, blob } = await buildStoryGraphic({
        title,
        subtitle,
        image,
        host: destinationHost,
        brand: brandLabel.toLowerCase().replace(/\s+/g, ''),
      });

      // Pre-copy link so the user just pastes into Link Sticker afterwards
      try { await navigator.clipboard.writeText(shareUrl); setCopyState('copied'); } catch { /* ignore */ }

      // Web Share Level 2 — supported on iOS Safari 15+, Chrome Android
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: title || 'Dreams League',
            text: 'Share this on your story — link is already copied to paste into the Link Sticker.',
          });
          setStoryState('shared');
        } catch (err) {
          if (err?.name === 'AbortError') { setStoryState('idle'); return; }
          // share rejected for some other reason — fall through to download
          throw err;
        }
      } else {
        // Fallback: trigger a download to camera roll / Downloads folder
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        setStoryState('downloaded');
      }
    } catch (e) {
      console.error('[InstagramShareModal] story graphic failed', e);
      setStoryErr(e?.message || 'Could not build the graphic. Try again.');
      setStoryState('error');
    } finally {
      setTimeout(() => setStoryState((s) => (s === 'generating' ? 'idle' : s)), 100);
      setTimeout(() => setStoryState('idle'), 2400);
    }
  };

  // ── Send as Message (clickable link, no file) ─────────────────────
  // Story-share hands IG a FILE so the user can drop a Link Sticker — but
  // if the recipient gets it via DM/Messages it lands as a static image.
  // This path uses URL-only Web Share so the OS share sheet routes to
  // iMessage / IG Direct / WhatsApp / SMS as a CLICKABLE rich-link card
  // (server-rendered OG tags supply the graphic preview + title). Tap
  // and the recipient is dropped straight into the story.
  const handleShareAsMessage = async () => {
    if (msgState === 'sharing') return;
    setMsgState('sharing');
    const payload = {
      url: shareUrl,
      title: title || brandLabel,
      text: subtitle
        ? `${title ? title + ' — ' : ''}${subtitle}`
        : (title || `Read this on ${brandLabel}`),
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(payload);
        setMsgState('sent');
      } else if (navigator?.clipboard?.writeText) {
        // Desktop fallback — copy a ready-to-paste line so the user can
        // drop it straight into any chat window.
        const line = `${payload.text}\n${shareUrl}`;
        await navigator.clipboard.writeText(line);
        setMsgState('copied');
      } else {
        window.location.href = `sms:?&body=${encodeURIComponent(payload.text + ' ' + shareUrl)}`;
        setMsgState('sent');
      }
    } catch (err) {
      if (err?.name === 'AbortError') { setMsgState('idle'); return; }
      setMsgState('error');
    } finally {
      setTimeout(() => setMsgState('idle'), 2200);
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Share to Instagram"
      data-testid="ig-share-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close share dialog"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fadeIn"
        data-testid="ig-share-backdrop"
      />

      {/* Sheet */}
      <div
        className="relative w-full sm:max-w-md bg-neutral-950 text-white rounded-t-3xl sm:rounded-3xl border border-white/10 shadow-2xl overflow-hidden animate-slideUp"
        style={{ maxHeight: 'min(92vh, calc(100vh - env(safe-area-inset-top) - 24px))' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
              <Instagram className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-white/55 font-bold">Share to Instagram</p>
              <p className="text-sm font-bold">Link Sticker preview</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            data-testid="ig-share-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body — iter482 · Bottom padding accounts for the
            iOS home indicator + tab bar so the second CTA button
            ("Send as Message") is fully reachable without hitting the
            OS chrome. Sheet maxHeight also clamps to the visible
            viewport so tall content scrolls INSIDE the sheet rather
            than pushing controls off-screen. */}
        <div
          className="px-5 overflow-y-auto"
          style={{
            maxHeight: 'calc(min(92vh, 100vh - env(safe-area-inset-top) - 24px) - 72px)',
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          }}
        >
          {/* IG Story phone mock with link sticker — iter482 · shrunk
              from max-w-300 → max-w-[240px] and margin trimmed to my-1
              so on iPhone SE / 12-mini the CTAs land inside the first
              viewport without scrolling. */}
          <div className="relative mx-auto my-1 w-full max-w-[240px] sm:max-w-[280px] aspect-[9/16] rounded-[28px] overflow-hidden ring-1 ring-white/10 bg-black">
            {/* Backdrop = cover image, blurred */}
            {image && !imgError ? (
              <img loading="lazy" decoding="async"
                src={image}
                alt=""
                aria-hidden="true"
                onError={() => setImgError(true)}
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-md opacity-70"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-orange-600/40 via-pink-600/40 to-purple-700/40" />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/60" />

            {/* Dark "Liquid Glass" profile card preview — mirrors the
                generated PNG (athlete profile-card aesthetic). */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[86%]">
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/15 bg-[#0e0e12]/85 backdrop-blur-xl shadow-[0_18px_40px_-10px_rgba(0,0,0,0.7)]">
                {/* Hero strip */}
                <div className="relative w-full h-24 bg-center bg-cover" style={image && !imgError ? { backgroundImage: `url(${image})` } : { background: 'linear-gradient(135deg,#2a1133,#3a0024,#1a0033)' }}>
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/85" />
                  <span className="absolute left-2 top-2 inline-flex items-center text-[8px] font-extrabold uppercase tracking-[0.18em] text-white/90 bg-black/40 ring-1 ring-white/15 backdrop-blur px-2 py-0.5 rounded-full">
                    {brandLabel} · Network
                  </span>
                </div>
                {/* Avatar overlap */}
                <div className="relative -mt-7 flex justify-center">
                  {image && !imgError ? (
                    <img loading="lazy" decoding="async"
                      src={image}
                      alt=""
                      aria-hidden="true"
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white/40 shadow-lg"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-white/10 ring-2 ring-white/40" />
                  )}
                </div>
                {/* Text */}
                <div className="px-3 pt-1.5 pb-3 text-center">
                  <p className="text-[7px] font-bold tracking-[0.22em] uppercase text-white/55">
                    {brandLabel} · Network
                  </p>
                  <p className="font-display uppercase text-white text-base leading-tight mt-0.5 line-clamp-2">
                    {title || 'Unnamed Athlete'}
                  </p>
                  {subtitle && (
                    <p className="font-italic text-[10px] text-white/75 mt-1 line-clamp-2 italic">{subtitle}</p>
                  )}
                  {/* glass action pills */}
                  <div className="mt-2 flex items-center justify-center gap-1">
                    {['Follow', 'Updates', 'Share'].map((p) => (
                      <span key={p} className="text-[7px] font-bold uppercase tracking-wider text-white/90 bg-white/10 ring-1 ring-white/20 px-2 py-0.5 rounded-full">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Link footer */}
                <div className="px-3 pb-2.5 flex items-center gap-1.5 border-t border-white/8 pt-2">
                  <div className="w-3.5 h-3.5 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shrink-0">
                    <span className="block w-1 h-1 rounded-full bg-white" />
                  </div>
                  <span className="text-[8px] font-extrabold uppercase tracking-wider text-white/85">LINK</span>
                  <span className="ml-auto text-[8px] text-white/55 truncate max-w-[120px]">{destinationHost}</span>
                </div>
              </div>
              <div className="mt-2 text-center">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white/90 bg-white/10 ring-1 ring-white/20 backdrop-blur px-2.5 py-1 rounded-full">
                  <Sparkles className="w-2.5 h-2.5" />
                  Tap to read
                </span>
              </div>
            </div>

            {/* Faux IG story top bar */}
            <div className="absolute top-3 left-3 right-3 flex gap-1">
              <span className="flex-1 h-0.5 rounded-full bg-white/80" />
              <span className="flex-1 h-0.5 rounded-full bg-white/30" />
              <span className="flex-1 h-0.5 rounded-full bg-white/30" />
            </div>
            <div className="absolute top-6 left-3 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white/20 ring-2 ring-white/60" />
              <span className="text-[11px] font-bold text-white drop-shadow">@{brandLabel.toLowerCase().replace(/\s+/g, '')}</span>
            </div>
          </div>

          {/* Step instructions */}
          <ol className="mt-4 space-y-2 text-sm">
            <li className="flex gap-3 items-start">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-white text-black font-extrabold text-xs flex items-center justify-center shrink-0">1</span>
              <span className="text-white/85"><b>Story</b> → tap <b>Send to my Story</b> (graphic + sticker).<br /><b>DM / iMessage</b> → tap <b>Send as Message</b> (clickable link card).</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-white text-black font-extrabold text-xs flex items-center justify-center shrink-0">2</span>
              <span className="text-white/85">In Instagram, tap the sticker icon → choose <b>Link</b>.</span>
            </li>
            <li className="flex gap-3 items-start">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-white text-black font-extrabold text-xs flex items-center justify-center shrink-0">3</span>
              <span className="text-white/85">Long-press → <b>Paste</b> (link is already copied). Post.</span>
            </li>
          </ol>

          {/* Primary: Auto-share the graphic to Story */}
          <button
            type="button"
            onClick={handleShareStoryGraphic}
            disabled={storyState === 'generating'}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-extrabold text-sm text-white transition active:scale-[0.98] disabled:opacity-70"
            style={{
              background: 'linear-gradient(95deg, #f9ce34 0%, #ee2a7b 45%, #6228d7 100%)',
              boxShadow: '0 10px 30px rgba(238, 42, 123, 0.35)',
            }}
            data-testid="ig-share-story-graphic-btn"
          >
            {storyState === 'generating' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Building graphic…</>
            ) : storyState === 'shared' ? (
              <><Check className="w-4 h-4" /> Sent to Instagram</>
            ) : storyState === 'downloaded' ? (
              <><Check className="w-4 h-4" /> Saved — open IG Story</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Send to my Story</>
            )}
          </button>

          {storyErr && (
            <p className="mt-2 text-[11px] text-red-300/90 text-center" data-testid="ig-share-story-err">{storyErr}</p>
          )}

          {/* Send as Message — clickable rich-link card (no file).
              Routes to iMessage / IG Direct / WhatsApp / SMS via the OS
              share sheet. Recipient gets the OG graphic preview AND a
              tappable link, so they open the story in one tap. */}
          <button
            type="button"
            onClick={handleShareAsMessage}
            disabled={msgState === 'sharing'}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-extrabold text-sm text-white bg-white/10 hover:bg-white/15 border border-white/15 transition active:scale-[0.98] disabled:opacity-70"
            data-testid="ig-share-message-btn"
            aria-label="Send as a clickable message"
          >
            {msgState === 'sharing' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Opening share…</>
            ) : msgState === 'sent' ? (
              <><Check className="w-4 h-4" /> Sent</>
            ) : msgState === 'copied' ? (
              <><Check className="w-4 h-4" /> Link copied — paste anywhere</>
            ) : msgState === 'error' ? (
              <><Send className="w-4 h-4" /> Try again</>
            ) : (
              <><MessageCircle className="w-4 h-4" /> Send as Message <span className="text-white/55 text-[11px] font-bold">· clickable link</span></>
            )}
          </button>

          {/* Secondary: explicit download (always works, no native share needed) */}
          <button
            type="button"
            onClick={async () => {
              setStoryErr(''); setStoryState('generating');
              try {
                const { blob, file } = await buildStoryGraphic({
                  title, subtitle, image,
                  host: destinationHost,
                  brand: brandLabel.toLowerCase().replace(/\s+/g, ''),
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = file.name;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 4000);
                setStoryState('downloaded');
              } catch (e) {
                setStoryErr(e?.message || 'Download failed'); setStoryState('error');
              } finally {
                setTimeout(() => setStoryState('idle'), 2400);
              }
            }}
            disabled={storyState === 'generating'}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-xs text-white bg-white/10 hover:bg-white/15 transition active:scale-[0.98] disabled:opacity-70"
            data-testid="ig-share-download-btn"
          >
            <Download className="w-3.5 h-3.5" /> Download graphic instead
          </button>

          {/* The link itself */}
          <div className="mt-4 bg-white/5 border border-white/10 rounded-2xl p-3 flex items-center gap-2">
            <code className="flex-1 text-[12px] text-white/80 truncate font-mono" data-testid="ig-share-url">
              {shareUrl}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-black font-bold text-xs hover:bg-gray-100 transition active:scale-95"
              data-testid="ig-share-copy-btn"
            >
              {copyState === 'copied' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copyState === 'copied' ? 'Copied' : 'Copy'}
            </button>
          </div>

          {/* Open Instagram CTA — fallback for users who don't want to share an image */}
          <button
            type="button"
            onClick={handleOpenInstagram}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-extrabold text-sm text-white bg-white/10 hover:bg-white/15 border border-white/15 transition active:scale-[0.98]"
            data-testid="ig-share-open-btn"
          >
            <Instagram className="w-4 h-4" />
            Just open Instagram
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
          </button>

          <p className="mt-3 text-[11px] leading-snug text-white/45 text-center">
            <b className="text-white/65">Send to my Story</b> uploads the graphic above straight into a new Instagram Story.
            Drop a Link Sticker on top and your link is already copied — long-press paste, post, done.
          </p>
        </div>
      </div>

      {/* Local animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn .18s ease-out both; }
        .animate-slideUp { animation: slideUp .22s cubic-bezier(.2,.8,.2,1) both; }
      `}</style>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};

export default InstagramShareModal;
